/* eslint-disable @typescript-eslint/no-shadow */
import { useMutation } from '@apollo/client';
import { useEffect, useState } from 'react';
import type { SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useRecoilValue, useSetRecoilState } from 'recoil';

import {
  CONTAINER_DELIVERIES_SERVICE_NAME,
  FAT_BONES_SERVICE_NAME,
  GREASE_TRAP_SERVICE_NAME,
  HOOD_CLEANING_SERVICE_NAME,
  OIL_DISPOSAL_SERVICE_NAME,
} from '../../config/constants';
import { formatDateWithTime } from '../../hooks/dateFormatter';
import ImageWithAuth from '../../hooks/useDriverSignature';
import { useRouteData } from '../../hooks/useRouteData';
import { ReactComponent as ArrowRigthIcon } from '../../icons/arrowcircleright.svg';
import { SET_STOP_AS_FINISH } from '../../services/graphql/stops/stopFinished';
import { driverStateAtom } from '../../services/recoil/atoms/driverState';
import { mutationStateAtom } from '../../services/recoil/atoms/mutationState';
import { routeStateAtom } from '../../services/recoil/atoms/routeState';
import { activeStopStateAtom } from '../../services/recoil/atoms/stopState';
import {
  formatNumber,
  formatNumberPercentage,
} from '../../utils/numberFormatter';
import { Loading } from '../atoms/Loader';
import SignatureCanvas from '../atoms/Signature';
import Title from '../atoms/Title';
import Layout from '../templates/Layout';
import { ContainerDeliveriesForm } from './driver-services/ContainerDeliveriesForm';
import { FatBonesForm } from './driver-services/FatBonesForm';
import { HoodCleaningForm } from './driver-services/HoodCleaningForm';
import { OilDisposalAndGreaseTrapForm } from './driver-services/OilDisposalAndGreaseTrapForm';

interface UpdateFormData {
  container_capacity: string;
  total_gallons: string;
  measuring_method: 'Metered' | 'Weighted' | 'Mathematical' | '';
  percentage_filled: string;
  client_signature: string;
  service: string;
  paymentType: string;
  pricePaid: string;
  paymentStatus: string;
  barrelsPickedUp: string;
  barrelsDropped: string;
  barrelSize: string;
  serviceContainerSizePickUp: string;
  serviceContainerSizeDropOff: string;
  serviceSpecialRequirements: string;
  comments: string;
  pricePerGallon: string;
  serviceType: string;
}

function Manifest() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const stop = useRecoilValue(activeStopStateAtom);
  const route = useRecoilValue(routeStateAtom);

  if (!route) {
    navigate('/dash');
    return null;
  }
  const currentDate = new Date();
  const formattedDate = formatDateWithTime(currentDate);
  const driver = useRecoilValue(driverStateAtom);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<UpdateFormData>({
    defaultValues: {
      total_gallons: '',
      measuring_method: '',
      percentage_filled: '',
      container_capacity: stop.serviceContainerSize
        ? `${formatNumber(stop.serviceContainerSize)} Gal`
        : '0 Gal',
      pricePaid: '',
      paymentType: stop.payment_type,
      service: '',
      paymentStatus: '',
      barrelsPickedUp: '',
      barrelsDropped: '',
      barrelSize: '',
      serviceContainerSizeDropOff: stop.serviceContainerSizeDropOff,
      serviceContainerSizePickUp: stop.serviceContainerSizePickUp,
      serviceSpecialRequirements: stop.serviceSpecialRequirements,
      comments: '',
      pricePerGallon: stop.pricePerGallon,
      serviceType: stop.serviceType || '',
    },
  });
  const { total_gallons: totalGallons, comments } = watch();

  const [signature, setSignature] = useState<File | null>(null);
  const [setStopAsFinish] = useMutation(SET_STOP_AS_FINISH);
  const [paymentType, setPaymentType] = useState(
    stop.payment_type ? [stop.payment_type] : [],
  );
  const [paidClient /* , setPaidClient */] = useState(stop.clientPaid);
  const [service, setService] = useState([]);
  const [containerDropOffSize, setContainerDropOffSize] = useState(
    stop.serviceContainerSizeDropOff,
  );
  const { refetch } = useRouteData(driver?.id);
  const setMutationState = useSetRecoilState(mutationStateAtom);
  const handleMutation = () => {
    setMutationState((prevState) => !prevState);
  };
  const [loading, setLoading] = useState(false);
  const [barrelSize, setDisplayBarrelSize] = useState(
    stop.serviceBarrelSize || 0,
  );

  console.log(errors);

  const onSubmit: SubmitHandler<UpdateFormData> = async (data) => {
    setLoading(true);
    await setStopAsFinish({
      variables: {
        stopId: stop.id,
        total_gallons: parseInt(data.total_gallons, 10),
        measuring_method: data.measuring_method,
        img_client_signature: signature,
        routeId: route.id,
        payment_type: String(paymentType),
        services: JSON.stringify(service),
        pricePaid: Number(data.pricePaid),
        payment_status: data.paymentStatus,
        barrelsPickedUp: Number(data.barrelsPickedUp),
        barrelsDropped: Number(data.barrelsDropped),
        barrelSize: Number(data.barrelSize),
        containerDropOffSize,
        specialRequirements: data.serviceSpecialRequirements,
        comments,
      },
    });
    await refetch();
    handleMutation();
    navigate('/firststop');
    setLoading(false);
  };

  useEffect(() => {
    if (stop.serviceContainerSize && totalGallons !== '') {
      const newPercentage = Number(totalGallons) / stop.serviceContainerSize;
      setValue('percentage_filled', `${formatNumberPercentage(newPercentage)}`);
    } else {
      setValue('percentage_filled', '0%');
    }
  }, [totalGallons]);

  useEffect(() => {
    const calculatedPrice = (
      Number(totalGallons || 0) * (stop.pricePerGallon || 0)
    ).toFixed(2);
    setValue('pricePaid', calculatedPrice as any);
  }, [totalGallons, stop.pricePerGallon]);

  useEffect(() => {
    setValue('barrelSize', stop.serviceBarrelSize || 0);
  }, [stop]);

  return (
    <Layout>
      {loading ? (
        <Loading size={50} />
      ) : (
        <>
          <Title text={t('manifest.title')} />
          <form
            className="flex flex-col gap-6 overflow-hidden"
            onSubmit={handleSubmit(onSubmit)}
          >
            <div className="flex flex-col gap-6">
              <h2 className="text-lg font-semibold text-ReyesBlack ">
                {stop.clientName}
              </h2>
              <span>
                <p className="text-sm text-ReyesBlack">{t('manifest.date')}</p>
                <p className="text-sm text-ReyesGrey">{formattedDate}</p>
              </span>
            </div>
            {stop.serviceStop === FAT_BONES_SERVICE_NAME && (
              <FatBonesForm
                control={control}
                errors={errors}
                stop={stop}
                barrelSize={barrelSize}
                setDisplayBarrelSize={setDisplayBarrelSize}
              />
            )}

            {(stop.serviceStop === OIL_DISPOSAL_SERVICE_NAME ||
              stop.serviceStop === GREASE_TRAP_SERVICE_NAME) && (
              <OilDisposalAndGreaseTrapForm
                control={control}
                errors={errors}
                paidClient={paidClient}
                paymentType={paymentType}
                route={route}
                pricePerGallon={stop.pricePerGallon}
              />
            )}
            {stop.serviceStop === HOOD_CLEANING_SERVICE_NAME && (
              <HoodCleaningForm
                control={control}
                setService={setService}
                service={service}
                errors={errors}
                paymentType={paymentType}
                setPaymentType={setPaymentType}
              />
            )}
            {stop.serviceStop === CONTAINER_DELIVERIES_SERVICE_NAME && (
              <ContainerDeliveriesForm
                control={control}
                stop={stop}
                containerDropOffSize={containerDropOffSize}
                setContainerDropOffSize={setContainerDropOffSize}
              />
            )}
            <span>
              <p className="text-sm text-ReyesBlack">
                {t('manifest.driverSignature')}
              </p>
              <ImageWithAuth />
            </span>
            <span>
              <p className="text-sm text-ReyesBlack">
                {t('manifest.clientSignature')}
              </p>
              <div className="flex w-full flex-col justify-center">
                <SignatureCanvas onSignatureSave={setSignature} />
              </div>
              <button
                type="submit"
                id="SaveManifest"
                className="mt-2 inline-flex h-12 w-full items-center justify-start gap-2.5 rounded-xl bg-ReyesGreen px-2.5 py-2 text-center text-lg font-black text-white shadow-custom"
              >
                {t('manifest.saveAndContinue')}
                <ArrowRigthIcon />
              </button>
            </span>
          </form>
        </>
      )}
    </Layout>
  );
}

export default Manifest;
